import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Download, Upload, Clock, CheckCircle, Send, Eye, Users, File, X, Loader2, FolderArchive, Search, Save, ChevronDown, XCircle } from 'lucide-react';

const DOCUMENT_CATEGORIES = [
  { key: 'izin_talepleri', label: 'İzin Talepleri' },
  { key: 'raporlar', label: 'Raporlar' },
  { key: 'savunmalar', label: 'Savunmalar' },
  { key: 'sikayetler', label: 'Şikayetler' },
  { key: 'tutanaklar', label: 'Tutanaklar' },
  { key: 'avanslar', label: 'Avanslar' },
  { key: 'kesintiler', label: 'Kesintiler' },
  { key: 'fazla_mesai', label: 'Fazla Mesai' }
];

const Documents = ({ readOnly = false }) => {
  const [activeTab, setActiveTab] = useState('belgeler');
  const [documentRequests, setDocumentRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Search states
  const [searchBelgeler, setSearchBelgeler] = useState('');
  const [searchArsiv, setSearchArsiv] = useState('');
  
  // Personel Form Archive
  const [personelForms, setPersonelForms] = useState([]);
  const [showCreateArchiveModal, setShowCreateArchiveModal] = useState(false);
  const [archiveTitle, setArchiveTitle] = useState('');
  const [archiveDescription, setArchiveDescription] = useState('');
  const [archiveFiles, setArchiveFiles] = useState([]);
  const [showDeleteArchiveDialog, setShowDeleteArchiveDialog] = useState(false);
  const [selectedArchiveForm, setSelectedArchiveForm] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form states
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchEmployee, setSearchEmployee] = useState('');
  const [additionalDocuments, setAdditionalDocuments] = useState([]); // Gönderirken ek belge
  
  // Kabul Et ve Kaydet states
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(null); // "empId-docIdx" key
  const [savingDoc, setSavingDoc] = useState(null);
  const [savedDocs, setSavedDocs] = useState({}); // {"empId-docIdx-category": true} kayıt takibi
  
  // Reddet states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchDocumentRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/document-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentRequests(data);
      }
    } catch (error) {
      console.error('Belge talepleri yüklenemedi:', error);
    }
  }, [API_URL]);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Çalışanlar yüklenemedi:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchDocumentRequests();
    fetchEmployees();
    fetchPersonelForms();
  }, [fetchDocumentRequests, fetchEmployees]);

  // documentRequests güncellenince açık modal varsa selectedRequest'i de güncelle
  useEffect(() => {
    if (selectedRequest && showDetailModal) {
      const updated = documentRequests.find(r => r.id === selectedRequest.id);
      if (updated) setSelectedRequest(updated);
    }
  }, [documentRequests]);


  // Belgeyi çalışan evraklarına kaydet
  const handleSaveDocToCategory = async (empId, doc, category, sendId) => {
    const dropdownKey = `${empId}-${sendId || doc.name}`;
    setSavingDoc(dropdownKey);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/employees/${empId}/documents-detail/category-add`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: category,
          name: doc.name || doc.filename || 'Belge',
          file_url: doc.data || doc.url || '',
          source_request_id: selectedRequest?.id || null
        })
      });
      if (response.ok) {
        const catLabel = DOCUMENT_CATEGORIES.find(c => c.key === category)?.label || category;
        toast.success(`Belge "${catLabel}" kategorisine kabul edildi ve kaydedildi`);
        setSavedDocs(prev => ({ ...prev, [`${empId}-${doc.name}-${category}`]: true }));
        setOpenCategoryDropdown(null);
        
        // Gönderim kaydını kabul edildi olarak işaretle
        if (sendId && selectedRequest?.id) {
          const acceptResp = await fetch(`${API_URL}/api/hr/document-requests/${selectedRequest.id}/send/${sendId}/accept`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (acceptResp.ok) {
            // Modal'ı doğrudan güncelle - selectedRequest'in send_history'sini değiştir
            setSelectedRequest(prev => {
              if (!prev) return prev;
              const updatedHistory = prev.send_history.map(s => 
                s.id === sendId ? { ...s, accepted: true, accepted_at: new Date().toISOString(), accepted_by: 'İK' } : s
              );
              return { ...prev, send_history: updatedHistory };
            });
          }
          // Arka planda listeyi de güncelle
          fetchDocumentRequests();
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Belge kaydedilemedi');
      }
    } catch (error) {
      console.error('Belge kaydetme hatası:', error);
      toast.error('Belge kaydedilemedi');
    }
    setSavingDoc(null);
  };

  // Belgeyi reddet
  const handleRejectSend = async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error('Red sebebi yazmalısınız');
      return;
    }
    setRejecting(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/document-requests/${selectedRequest.id}/send/${rejectTarget.sendId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() })
      });
      if (response.ok) {
        toast.success('Belge reddedildi');
        setShowRejectModal(false);
        setRejectReason('');
        setRejectTarget(null);
        // Modal'ı doğrudan güncelle
        setSelectedRequest(prev => {
          if (!prev) return prev;
          const updatedHistory = prev.send_history.map(s =>
            s.id === rejectTarget.sendId ? { ...s, rejected: true, rejected_at: new Date().toISOString(), rejected_by: 'İK', rejection_reason: rejectReason.trim() } : s
          );
          return { ...prev, send_history: updatedHistory };
        });
        fetchDocumentRequests();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Reddedilemedi');
      }
    } catch (error) {
      toast.error('Reddedilemedi');
    }
    setRejecting(false);
  };


  // Fetch personel forms archive
  const fetchPersonelForms = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/personel-forms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPersonelForms(data);
      }
    } catch (error) {
      console.error('Personel formları yüklenemedi:', error);
    }
  };

  // Archive file handling
  const handleArchiveFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setArchiveFiles(prev => [...prev, {
          name: file.name,
          data: reader.result,
          type: file.type,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeArchiveFile = (index) => {
    setArchiveFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateArchiveForm = async () => {
    if (!archiveTitle.trim()) {
      toast.error('Başlık gerekli');
      return;
    }
    if (archiveFiles.length === 0) {
      toast.error('En az bir dosya eklemelisiniz');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/personel-forms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: archiveTitle,
          description: archiveDescription,
          documents: archiveFiles
        })
      });

      if (response.ok) {
        toast.success('Form arşive eklendi');
        setShowCreateArchiveModal(false);
        setArchiveTitle('');
        setArchiveDescription('');
        setArchiveFiles([]);
        fetchPersonelForms();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Form eklenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArchiveForm = async () => {
    if (!selectedArchiveForm) return;

    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/personel-forms/${selectedArchiveForm.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Form silindi');
        setShowDeleteArchiveDialog(false);
        setSelectedArchiveForm(null);
        fetchPersonelForms();
      } else {
        toast.error('Form silinemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const filePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            type: file.type,
            data: reader.result
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(filePromises).then(fileData => {
      setNewFiles(prev => [...prev, ...fileData]);
    });
  };

  const removeFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateDocument = async () => {
    if (!newTitle.trim()) {
      toast.error('Belge başlığı zorunludur');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/document-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newTitle,
          description: newDescription,
          documents: newFiles,
          target_employee_ids: []
        })
      });

      if (response.ok) {
        toast.success('Belge formu oluşturuldu');
        setShowCreateModal(false);
        resetForm();
        fetchDocumentRequests();
      } else {
        toast.error('Belge formu oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToEmployees = async () => {
    if (!selectedRequest?.title?.trim()) {
      toast.error('Belge başlığı zorunludur');
      return;
    }
    if (selectedEmployees.length === 0) {
      toast.error('En az bir çalışan seçmelisiniz');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // PUT ile mevcut belgeyi güncelle (duplicate oluşturmaz)
      const response = await fetch(`${API_URL}/api/hr/document-requests/${selectedRequest.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: selectedRequest.title,
          description: selectedRequest.description,
          documents: additionalDocuments, // Yeni eklenen belgeler
          target_employee_ids: selectedEmployees // Yeni seçilen çalışanlar
        })
      });

      if (response.ok) {
        toast.success(`${selectedEmployees.length} çalışana gönderildi`);
        setShowSendModal(false);
        setSelectedEmployees([]);
        setAdditionalDocuments([]); // Ek belgeleri temizle
        fetchDocumentRequests();
      } else {
        toast.error('Gönderilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/hr/document-requests/${selectedRequest.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Belge talebi silindi');
        setShowDeleteDialog(false);
        setSelectedRequest(null);
        fetchDocumentRequests();
      } else {
        toast.error('Silinemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewTitle('');
    setNewDescription('');
    setNewFiles([]);
    setSelectedEmployees([]);
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(searchEmployee.toLowerCase()) ||
    emp.branch_name?.toLowerCase().includes(searchEmployee.toLowerCase())
  );

  // Belgeler sekmesi için filtreleme
  const filteredDocumentRequests = useMemo(() => {
    if (!searchBelgeler.trim()) return documentRequests;
    const searchLower = searchBelgeler.toLowerCase();
    return documentRequests.filter(req => 
      req.title?.toLowerCase().includes(searchLower) ||
      req.description?.toLowerCase().includes(searchLower)
    );
  }, [documentRequests, searchBelgeler]);

  // Arşiv sekmesi için filtreleme
  const filteredPersonelForms = useMemo(() => {
    if (!searchArsiv.trim()) return personelForms;
    const searchLower = searchArsiv.toLowerCase();
    return personelForms.filter(form => 
      form.title?.toLowerCase().includes(searchLower) ||
      form.description?.toLowerCase().includes(searchLower) ||
      form.created_by_name?.toLowerCase().includes(searchLower)
    );
  }, [personelForms, searchArsiv]);

  const getStatusBadge = (request) => {
    const responses = Object.keys(request.responses || {}).length;
    const targets = (request.target_employee_ids || []).length;
    
    if (targets === 0) {
      return <Badge variant="outline" className="bg-gray-100">Taslak</Badge>;
    }
    if (responses === 0) {
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Bekliyor</Badge>;
    }
    if (responses < targets) {
      return <Badge variant="outline" className="bg-blue-100 text-blue-800">{responses}/{targets} Cevap</Badge>;
    }
    return <Badge variant="outline" className="bg-green-100 text-green-800">Tamamlandı</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[hsl(var(--foreground))]">Belgeler</h2>
          <p className="text-[hsl(var(--muted-foreground))]">Belge talepleri oluşturun ve çalışanlara gönderin</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="belgeler" className="gap-2" data-testid="tab-belgeler">
            <FileText className="h-4 w-4" />
            BELGELER
          </TabsTrigger>
          <TabsTrigger value="arsiv" className="gap-2" data-testid="tab-arsiv">
            <FolderArchive className="h-4 w-4" />
            PERSONEL TALEP FORMLARI ARŞİVİ
            {personelForms.length > 0 && (
              <Badge className="ml-1 bg-green-500 text-white">{personelForms.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* BELGELER Tab */}
        <TabsContent value="belgeler" className="mt-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            {/* Arama Çubuğu */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="Belge ara... (başlık, açıklama)"
                value={searchBelgeler}
                onChange={(e) => setSearchBelgeler(e.target.value)}
                className="pl-10"
                data-testid="search-belgeler-input"
              />
            </div>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-[hsl(var(--brand-600))] hover:bg-[hsl(var(--brand-700))]"
              data-testid="create-document-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Belge Formu
            </Button>
          </div>

          {/* Document Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocumentRequests.length === 0 && searchBelgeler.trim() ? (
              <Card className="col-span-full">
                <CardContent className="py-16 text-center">
                  <Search className="h-16 w-16 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-30" />
                  <p className="text-[hsl(var(--muted-foreground))]">Arama sonucu bulunamadı</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">"{searchBelgeler}" için sonuç yok</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setSearchBelgeler('')}
                  >
                    Aramayı Temizle
                  </Button>
                </CardContent>
              </Card>
            ) : documentRequests.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-16 text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-30" />
                  <p className="text-[hsl(var(--muted-foreground))]">Henüz belge formu oluşturulmamış</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Belge Formunu Oluştur
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredDocumentRequests.map((request) => (
                <Card 
                  key={request.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer border-[hsl(var(--border))]"
                  data-testid={`document-card-${request.id}`}
                >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[hsl(var(--brand-600))]" />
                    <CardTitle className="text-lg">{request.title}</CardTitle>
                  </div>
                  {getStatusBadge(request)}
                </div>
                {request.description && (
                  <CardDescription className="mt-1">{request.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Files */}
                {request.documents && request.documents.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                    <File className="h-4 w-4" />
                    <span>{request.documents.length} dosya</span>
                  </div>
                )}
                
                {/* Target info */}
                <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                  <Users className="h-4 w-4" />
                  <span>
                    {(request.send_history || []).length} kez gönderildi
                  </span>
                </div>

                {/* Response info */}
                {(() => {
                  const totalSends = (request.send_history || []).length;
                  const responseCount = Object.keys(request.responses || {}).length;
                  const pendingCount = totalSends - responseCount;
                  return (
                    <>
                      {responseCount > 0 && (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span>{responseCount} cevap geldi</span>
                        </div>
                      )}
                      {pendingCount > 0 && (
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                          <Clock className="h-4 w-4" />
                          <span>{pendingCount} cevap bekleniyor</span>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetailModal(true);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Görüntüle
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowSendModal(true);
                    }}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Gönder
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Document Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Yeni Belge Formu Oluştur
            </DialogTitle>
            <DialogDescription>
              Çalışanlara göndermek için belge formu oluşturun
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Belge Başlığı *</Label>
              <Input
                id="title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Örn: Savunma Talebi"
                data-testid="document-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Belge hakkında açıklama..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Belgeler</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-[hsl(var(--muted-foreground))]" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Dosya yüklemek için tıklayın
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    PDF, DOC, DOCX, JPG, PNG
                  </p>
                </label>
              </div>
              
              {newFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {newFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-[hsl(var(--brand-600))]" />
                        <span className="text-sm">{file.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="text-red-600 h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateDocument}
              disabled={loading || !newTitle.trim()}
              className="bg-[hsl(var(--brand-600))] hover:bg-[hsl(var(--brand-700))]"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Employees Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Çalışanlara Gönder
            </DialogTitle>
            <DialogDescription>
              Belge detaylarını kontrol edin ve göndermek istediğiniz çalışanları seçin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Başlık Alanı */}
            <div className="space-y-2">
              <Label htmlFor="send-title">Belge Başlığı *</Label>
              <Input
                id="send-title"
                value={selectedRequest?.title || ''}
                onChange={(e) => setSelectedRequest(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Belge başlığı"
                data-testid="send-title-input"
              />
            </div>
            
            {/* Açıklama Alanı */}
            <div className="space-y-2">
              <Label htmlFor="send-description">Açıklama</Label>
              <Textarea
                id="send-description"
                value={selectedRequest?.description || ''}
                onChange={(e) => setSelectedRequest(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Belge hakkında açıklama yazın..."
                rows={3}
                data-testid="send-description-input"
              />
            </div>
            
            {/* Çalışan Seçimi */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Çalışan Seçin *</Label>
              <Input
                placeholder="Çalışan ara..."
                value={searchEmployee}
                onChange={(e) => setSearchEmployee(e.target.value)}
              />
            </div>
            
            <ScrollArea className="h-[300px] border rounded-lg p-2">
              {filteredEmployees.map((emp) => (
                <div
                  key={emp.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                    selectedEmployees.includes(emp.id) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => toggleEmployeeSelection(emp.id)}
                >
                  <Checkbox
                    checked={selectedEmployees.includes(emp.id)}
                    onCheckedChange={() => toggleEmployeeSelection(emp.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{emp.name}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{emp.branch_name}</p>
                  </div>
                </div>
              ))}
            </ScrollArea>
            
            {selectedEmployees.length > 0 && (
              <p className="text-sm text-[hsl(var(--brand-600))]">
                {selectedEmployees.length} çalışan seçildi
              </p>
            )}
            
            {/* Ek Belge Yükleme */}
            <div className="space-y-2 pt-4 border-t">
              <Label className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Ek Belge Ekle (Opsiyonel)
              </Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const readFiles = [];
                  let processed = 0;
                  files.forEach((file) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      readFiles.push({ name: file.name, type: file.type, data: reader.result });
                      processed++;
                      if (processed === files.length) {
                        setAdditionalDocuments(prev => [...prev, ...readFiles]);
                      }
                    };
                    reader.readAsDataURL(file);
                  });
                }}
                className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[hsl(var(--brand-50))] file:text-[hsl(var(--brand-700))]"
              />
              {additionalDocuments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {additionalDocuments.map((doc, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      <File className="h-3 w-3" />
                      {doc.name}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-red-500" 
                        onClick={() => setAdditionalDocuments(prev => prev.filter((_, i) => i !== idx))}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSendModal(false); setSelectedEmployees([]); setAdditionalDocuments([]); }}>
              İptal
            </Button>
            <Button 
              onClick={handleSendToEmployees}
              disabled={loading || selectedEmployees.length === 0}
              className="bg-[hsl(var(--brand-600))] hover:bg-[hsl(var(--brand-700))]"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Gönder ({selectedEmployees.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => { setShowDetailModal(open); if (!open) setOpenCategoryDropdown(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              {selectedRequest?.title}
            </DialogTitle>
            {selectedRequest?.description && (
              <DialogDescription>{selectedRequest.description}</DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Gönderim Bilgileri */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Send className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">Gönderen:</span>
                <span className="text-blue-700">{selectedRequest?.created_by_name || 'İK'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">Gönderilme Tarihi:</span>
                <span className="text-blue-700">
                  {selectedRequest?.created_at ? new Date(selectedRequest.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </span>
              </div>
            </div>

            {/* Gönderilen Belgeler */}
            {selectedRequest?.documents && selectedRequest.documents.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <File className="h-4 w-4" />
                  Gönderilen Belgeler ({selectedRequest.documents.length})
                </Label>
                <div className="space-y-1">
                  {selectedRequest.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-[hsl(var(--brand-600))]" />
                        <span className="text-sm">{doc.name || `Dosya ${index + 1}`}</span>
                      </div>
                      <a
                        href={doc.data || doc.url || '#'}
                        download={doc.name || `dosya_${index + 1}`}
                        className="p-1 rounded hover:bg-gray-200 transition-colors"
                        data-testid={`sent-doc-download-${index}`}
                      >
                        <Download className="h-4 w-4 text-gray-600" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Gönderilen Kişiler ve Cevap Durumları */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                Gönderim Listesi ({(selectedRequest?.send_history || []).length})
              </Label>
              <div className="border rounded-lg divide-y">
                {[...(selectedRequest?.send_history || [])]
                  .sort((a, b) => {
                    // Kabul edilenler ve reddedilenler en alta
                    const aFinished = a.accepted || a.rejected;
                    const bFinished = b.accepted || b.rejected;
                    if (aFinished && !bFinished) return 1;
                    if (!aFinished && bFinished) return -1;
                    return 0;
                  })
                  .map((send, sendIdx) => {
                  const empId = send.employee_id;
                  // Her gönderim kaydı bağımsız - responded flag'ine göre eşleştir
                  const isResponded = send.responded === true;
                  const response = isResponded ? selectedRequest?.responses?.[empId] : null;
                  const emp = employees.find(e => e.id === empId);
                  const empName = response?.employee_name || selectedRequest?.target_employee_names?.[empId] || emp?.name || empId;
                  const isAccepted = send.accepted === true;
                  const isRejected = send.rejected === true;
                  
                  return (
                    <div key={send.id || sendIdx} className={`p-3 ${isAccepted ? 'bg-green-50/50' : isRejected ? 'bg-red-50/50' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <span className="font-medium text-sm">{empName}</span>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            Talep Tarihi: {new Date(send.sent_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {send.description && (
                            <div className="text-xs text-gray-600 mt-0.5 italic">
                              {send.description}
                            </div>
                          )}
                        </div>
                        {isAccepted ? (
                          <Badge className="bg-blue-100 text-blue-800 text-xs" data-testid={`status-accepted-${sendIdx}`}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Kabul Edildi ve Kaydedildi
                          </Badge>
                        ) : isRejected ? (
                          <Badge className="bg-red-100 text-red-800 text-xs" data-testid={`status-rejected-${sendIdx}`}>
                            <XCircle className="h-3 w-3 mr-1" />
                            Reddedildi
                          </Badge>
                        ) : response ? (
                          <Badge className="bg-green-100 text-green-800 text-xs" data-testid={`status-responded-${sendIdx}`}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Cevaplandı
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid={`status-pending-${sendIdx}`}>
                            <Clock className="h-3 w-3 mr-1" />
                            Cevap Bekleniyor
                          </Badge>
                        )}
                      </div>
                      
                      {response && !isAccepted && !isRejected && (
                        <div className="mt-2 pl-3 border-l-2 border-green-300 space-y-2">
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            Cevap Tarihi: {new Date(response.responded_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {response.message && (
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{response.message}</p>
                          )}
                          {response.documents && response.documents.length > 0 && (
                            <div className="space-y-2">
                              {response.documents.map((doc, idx) => {
                                const dropdownKey = `${empId}-${send.id}-${idx}`;
                                return (
                                  <div key={idx} className="flex items-center gap-2 flex-wrap">
                                    <a
                                      href={doc.data ? doc.data : (doc.url?.startsWith('/api') ? `${API_URL}${doc.url}` : doc.url)}
                                      download={doc.name || doc.filename || `dosya_${idx + 1}`}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md hover:bg-blue-50 transition-colors cursor-pointer"
                                      data-testid={`response-doc-${sendIdx}-${idx}`}
                                    >
                                      <Download className="h-4 w-4 text-blue-600" />
                                      {doc.name || doc.filename || `Dosya ${idx + 1}`}
                                    </a>
                                    {!readOnly && (
                                      <div className="relative">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-8 text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50"
                                          onClick={() => setOpenCategoryDropdown(openCategoryDropdown === dropdownKey ? null : dropdownKey)}
                                          disabled={savingDoc === dropdownKey}
                                          data-testid={`accept-save-btn-${sendIdx}-${idx}`}
                                        >
                                          {savingDoc === dropdownKey ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                          ) : (
                                            <Save className="h-3 w-3" />
                                          )}
                                          Kabul Et ve Kaydet
                                          <ChevronDown className="h-3 w-3" />
                                        </Button>
                                        {openCategoryDropdown === dropdownKey && (
                                          <div className="absolute z-50 top-full left-0 mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[180px]"
                                            data-testid={`category-dropdown-${sendIdx}-${idx}`}
                                          >
                                            <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 border-b">
                                              Kategori Seçin
                                            </div>
                                            {DOCUMENT_CATEGORIES.map((cat) => {
                                              const alreadySaved = savedDocs[`${empId}-${(doc.name || doc.filename)}-${cat.key}`];
                                              return (
                                                <button
                                                  key={cat.key}
                                                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${alreadySaved ? 'bg-green-50 text-green-700' : 'hover:bg-blue-50'}`}
                                                  onClick={(e) => { e.stopPropagation(); handleSaveDocToCategory(empId, doc, cat.key, send.id); }}
                                                  data-testid={`category-option-${cat.key}-${sendIdx}-${idx}`}
                                                >
                                                  {cat.label}
                                                  {alreadySaved && <CheckCircle className="h-3.5 w-3.5 text-green-600" />}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {/* Reddet butonu */}
                          {!readOnly && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs gap-1 text-red-700 border-red-300 hover:bg-red-50 mt-1"
                              onClick={() => { setRejectTarget({ sendId: send.id, empName }); setRejectReason(''); setShowRejectModal(true); }}
                              data-testid={`reject-btn-${sendIdx}`}
                            >
                              <XCircle className="h-3 w-3" />
                              Reddet
                            </Button>
                          )}
                        </div>
                      )}
                      {isAccepted && (
                        <div className="mt-1 text-xs text-blue-600">
                          Kabul Tarihi: {new Date(send.accepted_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {send.accepted_by && ` - ${send.accepted_by}`}
                        </div>
                      )}
                      {isRejected && (
                        <div className="mt-1 space-y-0.5">
                          <div className="text-xs text-red-600">
                            Red Tarihi: {new Date(send.rejected_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {send.rejected_by && ` - ${send.rejected_by}`}
                          </div>
                          <div className="text-xs text-red-700 font-medium">
                            Sebep: {send.rejection_reason}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reddet Modalı */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="h-5 w-5" />
              Belgeyi Reddet
            </DialogTitle>
            <DialogDescription>
              {rejectTarget?.empName} adlı kişinin belgesi reddedilecek.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Red Sebebi *</Label>
            <textarea
              className="w-full mt-1 p-2 border rounded-md text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-red-300"
              placeholder="Red sebebini yazın..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              İptal
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRejectSend}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="reject-confirm-btn"
            >
              {rejecting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Belge Formunu Sil
            </AlertDialogTitle>
            <AlertDialogDescription>
              "{selectedRequest?.title}" belge formunu silmek istediğinize emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRequest}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

        </TabsContent>

        {/* ARŞİV Tab */}
        <TabsContent value="arsiv" className="mt-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            {/* Arama Çubuğu */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Form ara... (başlık, açıklama, oluşturan)"
                value={searchArsiv}
                onChange={(e) => setSearchArsiv(e.target.value)}
                className="pl-10"
                data-testid="search-arsiv-input"
              />
            </div>
            <Button 
              onClick={() => setShowCreateArchiveModal(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-archive-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Form Ekle
            </Button>
          </div>

          {/* Archive Info */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-700">
              Buraya yüklenen formlar tüm şubelerdeki çalışanlar tarafından görüntülenebilir ve indirilebilir.
              İzin talep formları, başvuru formları ve diğer standart belgeleri buradan paylaşabilirsiniz.
            </p>
          </div>

          {/* Archive Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPersonelForms.length === 0 && searchArsiv.trim() ? (
              <Card className="col-span-full">
                <CardContent className="py-16 text-center">
                  <Search className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">Arama sonucu bulunamadı</p>
                  <p className="text-sm text-gray-400 mt-1">"{searchArsiv}" için sonuç yok</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setSearchArsiv('')}
                  >
                    Aramayı Temizle
                  </Button>
                </CardContent>
              </Card>
            ) : personelForms.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-16 text-center">
                  <FolderArchive className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">Henüz form eklenmemiş</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateArchiveModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Formu Ekle
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredPersonelForms.map((form) => (
                <Card 
                  key={form.id} 
                  className="hover:shadow-lg transition-shadow border-green-200"
                  data-testid={`archive-card-${form.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <FolderArchive className="h-5 w-5 text-green-600" />
                        <CardTitle className="text-lg">{form.title}</CardTitle>
                      </div>
                      <Badge className="bg-green-100 text-green-700">
                        {form.documents?.length || 0} Dosya
                      </Badge>
                    </div>
                    {form.description && (
                      <CardDescription className="mt-1">{form.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="pt-2">
                    {/* Files List */}
                    {form.documents && form.documents.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {form.documents.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                            <div className="flex items-center gap-2">
                              <File className="h-4 w-4 text-green-600" />
                              <span className="text-sm truncate max-w-[150px]">{doc.name}</span>
                              <span className="text-xs text-gray-400">{formatFileSize(doc.size)}</span>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const newWindow = window.open();
                                  if (newWindow && doc.data) {
                                    if (doc.data.startsWith('data:image/')) {
                                      newWindow.document.write(`<html><head><title>${doc.name}</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;"><img src="${doc.data}" style="max-width:100%;max-height:100vh;" /></body></html>`);
                                    } else if (doc.data.startsWith('data:application/pdf')) {
                                      newWindow.document.write(`<html><head><title>${doc.name}</title></head><body style="margin:0;"><iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe></body></html>`);
                                    } else {
                                      newWindow.location.href = doc.data;
                                    }
                                  }
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  if (doc.data) {
                                    const link = document.createElement('a');
                                    link.href = doc.data;
                                    link.download = doc.name;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-gray-400">
                        {form.created_by_name} • {new Date(form.created_at).toLocaleDateString('tr-TR')}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setSelectedArchiveForm(form);
                          setShowDeleteArchiveDialog(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Archive Form Modal */}
      <Dialog open={showCreateArchiveModal} onOpenChange={setShowCreateArchiveModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderArchive className="h-5 w-5 text-green-600" />
              Yeni Form Ekle
            </DialogTitle>
            <DialogDescription>
              Tüm çalışanların görebileceği ve indirebileceği form ekleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="archive-title">Başlık *</Label>
              <Input
                id="archive-title"
                value={archiveTitle}
                onChange={(e) => setArchiveTitle(e.target.value)}
                placeholder="Örn: İzin Talep Formu"
                data-testid="archive-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="archive-description">Açıklama</Label>
              <Textarea
                id="archive-description"
                value={archiveDescription}
                onChange={(e) => setArchiveDescription(e.target.value)}
                placeholder="Form hakkında açıklama..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Dosyalar *</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleArchiveFileChange}
                  className="hidden"
                  id="archive-file-input"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <label htmlFor="archive-file-input" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">Dosya yüklemek için tıklayın</p>
                  <p className="text-xs text-gray-400">PDF, DOC, XLS, JPG, PNG</p>
                </label>
              </div>
              
              {archiveFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {archiveFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-green-600" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeArchiveFile(index)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateArchiveModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateArchiveForm}
              disabled={loading || !archiveTitle.trim() || archiveFiles.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Archive Confirmation */}
      <AlertDialog open={showDeleteArchiveDialog} onOpenChange={setShowDeleteArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Formu Sil
            </AlertDialogTitle>
            <AlertDialogDescription>
              "{selectedArchiveForm?.title}" formunu silmek istediğinize emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteArchiveForm}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Documents;
